import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, User, ShieldCheck, BarChart3 } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const CYAN_WORDS = new Set(["work", "ahead."]);

export const TRUST_BADGES = [
  { icon: User, l1: "Skilled Virtual", l2: "Assistants" },
  { icon: ShieldCheck, l1: "Skilled Remote", l2: "Support" },
  { icon: BarChart3, l1: "More Time for", l2: "What Matters" },
];

export default function Hero() {
  const { content, openBooking } = useSite();
  const hero = content.hero;

  // split headline into two balanced lines: "Your team, ready" / "for the work ahead."
  const words = hero.headline.split(" ");
  const half = Math.floor(words.length / 2);
  const line1 = words.slice(0, half).join(" ");
  const line2 = words.slice(half).join(" ");

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-3.5, 3.5]), { stiffness: 60, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section
      id="top"
      className="relative min-h-[700px] flex items-center"
      style={{ backgroundImage: "url(/hero-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div aria-hidden="true" className="hero-flow-bg" />
      <div aria-hidden="true" className="hero-arc-glow" />
      <div aria-hidden="true" className="hero-arc-line" />
      <div className="relative w-full max-w-[1400px] mx-auto px-5 lg:px-10 pt-[120px] pb-20 lg:pt-[130px] lg:pb-24 grid lg:grid-cols-[1fr_1.05fr] gap-12 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow eyebrow-dark mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', 'Nunito Sans', sans-serif" }}
          >
            Skilled support for your everyday operations
          </motion.div>

          <h1 className="hero-headline-font text-white text-4xl sm:text-5xl lg:text-[52px] xl:text-[58px] leading-[1.06] tracking-tight">
            {[line1, line2].map((line, li) => (
              <span key={li} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + li * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  style={{ textShadow: "0 2px 24px rgba(4,10,24,0.6)" }}
                >
                  {line.split(" ").map((w, wi) => (
                    <span key={wi} className={CYAN_WORDS.has(w.toLowerCase()) ? "text-[#00CFFF]" : ""}>{w}{" "}</span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-6 text-slate-100 text-base sm:text-lg max-w-lg leading-relaxed"
            style={{ textShadow: "0 1px 16px rgba(4,10,24,0.7)", fontFamily: "'DM Sans', 'Nunito Sans', sans-serif" }}
          >
            {hero.subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              className="btn btn-primary hero-cta-primary w-full sm:w-auto justify-center"
              style={{ fontFamily: "'Plus Jakarta Sans', 'Nunito Sans', sans-serif", fontWeight: 600 }}
              onClick={() => openBooking(null, "booking")}
              data-testid="hero-cta-consultation"
            >
              Book a Free Consultation <ArrowRight size={17} strokeWidth={2.5} />
            </button>
            <a
              href="#process"
              className="hero-cta-glass w-full sm:w-auto justify-center"
              data-testid="hero-cta-process"
              style={{ fontFamily: "'Plus Jakarta Sans', 'Nunito Sans', sans-serif", fontWeight: 700 }}
            >
              Explore Our Process
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex items-center justify-between flex-nowrap w-full sm:justify-start sm:gap-7"
            style={{ fontFamily: "'DM Sans', 'Nunito Sans', sans-serif" }}
          >
            {TRUST_BADGES.map((b, i) => (
              <div key={b.l1} className="flex items-center gap-2 sm:gap-7 flex-shrink-0">
                {i > 0 && <span className="hidden sm:block w-px h-10 bg-white/25" />}
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <b.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#00CFFF] flex-shrink-0" strokeWidth={2.2} />
                  <span className="text-[10.5px] sm:text-[13px] font-bold text-white leading-tight whitespace-nowrap" style={{ textShadow: "0 1px 12px rgba(4,10,24,0.8)" }}>{b.l1}<br />{b.l2}</span>
                </div>
              </div>
            ))}
          </motion.div>

          {hero.urgency_enabled !== false && hero.urgency_text && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="mt-8 flex items-center gap-3"
              style={{ fontFamily: "'DM Sans', 'Nunito Sans', sans-serif" }}
              data-testid="hero-urgency-text"
            >
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
              </span>
              <span className="text-sm text-white/90 font-semibold" style={{ textShadow: "0 1px 12px rgba(4,10,24,0.8)" }}>{hero.urgency_text}</span>
            </motion.div>
          )}

          {(content.client_countries || []).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="mt-8 flex items-center justify-center flex-nowrap gap-3 sm:gap-4 sm:justify-start pb-14 sm:pb-0"
              data-testid="hero-flags-row"
            >
              {content.client_countries.map((c, i) => (
                <div key={c.id || i} className="relative group flag-float flex-shrink-0" style={{ animationDelay: `${i * 0.65}s` }}>
                  <img
                    src={c.flag_url || "/flags/us.png"}
                    alt={c.name}
                    className="w-8 h-8 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-[rgba(0,207,255,0.55)] shadow-[0_0_18px_rgba(0,191,255,0.35)]"
                    data-testid={`hero-flag-${i}`}
                  />
                  <div className="flag-tooltip" data-testid={`hero-flag-tooltip-${i}`}>
                    <span className="font-bold text-white">{c.name}</span>
                    <span className="text-cyan-300"> — {c.clients_served} clients served</span>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.45, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 flex justify-center items-end lg:justify-end lg:self-stretch lg:items-end lg:-mb-24"
          onMouseMove={onMove}
          onMouseLeave={onLeave}
        >
          <motion.img
            src="/hero-laptop-silver.png"
            alt="TrustDesk premium laptop with specialist role cards"
            className="w-full h-auto lg:w-[125%] max-w-none lg:min-w-[600px] lg:-mr-8"
            style={{ objectFit: "contain", rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
            data-testid="hero-laptop-image"
          />
        </motion.div>
      </div>
    </section>
  );
}
